import { useState, type ReactNode } from "react";
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "@/packages/utils";
import { springs } from "../motion";
import { Button } from "../primitives";
import { Dropdown } from "../Dropdown";
import type { SortOption, SortState } from "./types";

interface SearchInputProps {
  filterSlot?: ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  placeholder?: string;
  sortOptions?: SortOption[];
  activeSort?: SortState;
  onSortChange?: (sort: SortState) => void;
}

export function SearchInput({
  filterSlot,
  searchValue,
  onSearchChange,
  placeholder,
  sortOptions,
  activeSort,
  onSortChange,
}: SearchInputProps) {
  const { t } = useTranslation(["common"]);
  const resolvedPlaceholder =
    placeholder ?? t("ui.pageHeader.searchPlaceholder");
  const [isFocused, setIsFocused] = useState(false);

  const activeSortLabel = sortOptions?.find(
    (o) => o.id === activeSort?.field,
  )?.label;

  return (
    <motion.div
      className="flex w-full items-center rounded-button border bg-surface"
      animate={{
        borderColor: isFocused ? "#E8734A" : "var(--color-border)",
        boxShadow: isFocused
          ? "0 0 0 3px rgba(232,115,74,0.1)"
          : "0 0 0 0px rgba(232,115,74,0)",
      }}
      transition={springs.snappy}
    >
      {/* Search field */}
      <div className="flex flex-1 items-center">
        <Search
          className="ml-3 h-4 w-4 shrink-0 text-muted"
          strokeWidth={1.8}
        />
        <input
          type="text"
          placeholder={resolvedPlaceholder}
          value={searchValue ?? ""}
          onChange={(e) => onSearchChange?.(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="h-9 w-full bg-transparent pl-2.5 pr-3 text-[14px] font-normal text-primary placeholder:text-muted outline-none"
        />
      </div>

      {/* Divider */}
      <div className="h-5 w-px bg-[#E5E5EA]" />

      {/* Inline actions */}
      <div className="flex items-center gap-0.5 pr-1.5">
        {sortOptions && sortOptions.length > 0 && onSortChange ? (
          <SortDropdown
            options={sortOptions}
            activeSort={activeSort}
            onSortChange={onSortChange}
            label={activeSortLabel}
          />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            icon={ArrowUpDown}
            className="rounded-[6px] px-2.5"
          >
            {t("labels.sortBy")}
          </Button>
        )}

        {filterSlot}
      </div>
    </motion.div>
  );
}

function SortDropdown({
  options,
  activeSort,
  onSortChange,
  label,
}: {
  options: SortOption[];
  activeSort?: SortState;
  onSortChange: (sort: SortState) => void;
  label?: string;
}) {
  const { t } = useTranslation(["common"]);
  return (
    <Dropdown>
      <Dropdown.Trigger>
        <Button
          variant="ghost"
          size="sm"
          icon={ArrowUpDown}
          className="rounded-[6px] px-2.5"
        >
          {label ?? t("labels.sortBy")}
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Content align="end" sideOffset={6}>
        <Dropdown.Label>{t("labels.sortBy")}</Dropdown.Label>
        {options.map((option) => {
          const isActive = activeSort?.field === option.id;
          return (
            <Dropdown.Item
              key={option.id}
              onSelect={() => {
                if (isActive) {
                  onSortChange({
                    field: option.id,
                    direction: activeSort.direction === "asc" ? "desc" : "asc",
                  });
                } else {
                  onSortChange({ field: option.id, direction: "asc" });
                }
              }}
              icon={
                isActive ? (
                  <Check className="h-4 w-4 text-accent" />
                ) : (
                  <span className="h-4 w-4" />
                )
              }
            >
              <span className="flex-1">{option.label}</span>
              {isActive &&
                (activeSort.direction === "asc" ? (
                  <ArrowUp className="ml-auto h-3.5 w-3.5 text-muted" />
                ) : (
                  <ArrowDown className="ml-auto h-3.5 w-3.5 text-muted" />
                ))}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Content>
    </Dropdown>
  );
}
